// src/components/modals/AnalyticsDashboardModal.tsx
// Phase 4: Analytics Dashboard Modal - COMPLETE CLEAN VERSION
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AIConfig, AgentProfile, TestResult } from '../../types';

interface TestExecution {
    id: string;
    agent_name: string;
    test_name: string;
    test_type: string;
    status: 'PASSED' | 'FAILED' | 'ERROR';
    ai_confidence: number;
    execution_time_ms: number;
    response_time_ms: number;
    failure_reason?: string;
    validation_analysis: string;
    tokens_used: number;
    cost_estimate: number;
    completed_at: string;
    started_at: string;
}

interface AnalyticsDashboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    aiConfig: AIConfig | null;
    agents: AgentProfile[];
    results: Map<string, TestResult>;
    selectedAgent: string;
}

const AnalyticsDashboardModal: React.FC<AnalyticsDashboardModalProps> = ({
    isOpen,
    onClose,
    aiConfig,
    agents,
    results,
    selectedAgent
}) => {
    const [activeTab, setActiveTab] = useState<'performance' | 'usage' | 'agents' | 'costs'>('performance');
    const [isLoading, setIsLoading] = useState(false);
    const [executionData, setExecutionData] = useState<TestExecution[]>([]);
    const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
    const [viewMode, setViewMode] = useState<'current' | 'historical'>('current');
    const [error, setError] = useState<string | null>(null);
    const [totalAIUsage, setTotalAIUsage] = useState({ tokens: 0, cost: 0 });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

    // Fetch total AI usage (like main window)
    const fetchTotalAIUsage = async () => {
        if (!selectedAgent) return;
        
        try {
            const response = await axios.get(`${API_BASE_URL}/api/token-usage/${selectedAgent}`);
            const data = response.data;
            
            // DEBUG: Log the API response to see what we're getting
            //console.log('Analytics Dashboard API Response:', data);
            
            let totalTokens = 0;
            let totalCost = 0;
            
            if (data.totals) {
                //console.log('Using totals format:', data.totals);
                totalTokens = data.totals.total_tokens || 0;
                totalCost = data.totals.total_cost || 0;
            } else if (data.tokens && data.costs) {
                //console.log('Using tokens/costs format:', data.tokens, data.costs);
                totalTokens = data.tokens.total || 0;
                totalCost = data.costs.total || 0;
            } else {
                //console.log('Using legacy format, data.total:', data.total);
                // Legacy format
                totalTokens = data.total || 0;
                totalCost = 0; // Legacy doesn't have costs
            }
            
            //console.log('Final totalTokens:', totalTokens, 'totalCost:', totalCost);
            setTotalAIUsage({ tokens: totalTokens, cost: totalCost });
        } catch (error) {
            console.error('Failed to fetch total AI usage:', error);
            setTotalAIUsage({ tokens: 0, cost: 0 });
        }
    };

    const generateReport = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Always fetch total AI usage first, regardless of executions
            await fetchTotalAIUsage();
            
            const executionsResponse = await axios.get(`${API_BASE_URL}/db/test-executions`);
            const allExecutions = executionsResponse.data || [];

            if (allExecutions.length === 0) {
                setError('No test execution data found. Run some tests to generate performance metrics.');
                setExecutionData([]);
                setIsLoading(false);
                return;
            }

            let filteredExecutions = allExecutions;

            if (viewMode === 'current') {
                const latestExecutions = new Map<string, TestExecution>();
                allExecutions.forEach((exec: TestExecution) => {
                    const key = `${exec.agent_name}_${exec.test_name}`;
                    if (!latestExecutions.has(key) ||
                        new Date(exec.completed_at || exec.started_at) >
                        new Date(latestExecutions.get(key)!.completed_at || latestExecutions.get(key)!.started_at)) {
                        latestExecutions.set(key, exec);
                    }
                });
                filteredExecutions = Array.from(latestExecutions.values());
            } else {
                const days = selectedTimeRange === '7d' ? 7 : selectedTimeRange === '30d' ? 30 : 90;
                const cutoffDate = new Date();
                cutoffDate.setDate(cutoffDate.getDate() - days);
                filteredExecutions = allExecutions.filter((exec: TestExecution) =>
                    new Date(exec.completed_at || exec.started_at) >= cutoffDate
                );
            }

            setExecutionData(filteredExecutions);
        } catch (error) {
            console.error('Error generating report:', error);
            setError('Failed to generate performance report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const calculateMetrics = (executions: TestExecution[], agentName?: string) => {
        const relevantExecutions = agentName
            ? executions.filter(e => e.agent_name === agentName)
            : executions;

        if (relevantExecutions.length === 0) {
            return {
                total: 0,
                passed: 0,
                failed: 0,
                successRate: 0,
                avgConfidence: 0,
                avgExecutionTime: 0,
                totalTokens: 0,
                totalCost: 0,
                failureReasons: []
            };
        }

        const passed = relevantExecutions.filter(e => e.status === 'PASSED').length;
        const failed = relevantExecutions.filter(e => e.status === 'FAILED').length;

        const failureReasons: { [key: string]: number } = {};
        relevantExecutions
            .filter(e => e.status === 'FAILED' && e.failure_reason)
            .forEach(e => {
                const reason = e.failure_reason || 'Unknown';
                failureReasons[reason] = (failureReasons[reason] || 0) + 1;
            });

        const totalTokens = relevantExecutions.reduce((sum, e) => sum + (e.tokens_used || 0), 0);
        const totalCost = relevantExecutions.reduce((sum, e) => sum + (e.cost_estimate || 0), 0);
        

        return {
            total: relevantExecutions.length,
            passed,
            failed,
            successRate: passed / relevantExecutions.length,
            avgConfidence: relevantExecutions.reduce((sum, e) => sum + (e.ai_confidence || 0), 0) / relevantExecutions.length,
            avgExecutionTime: relevantExecutions.reduce((sum, e) => sum + (e.execution_time_ms || 0), 0) / relevantExecutions.length,
            totalTokens,
            totalCost,
            failureReasons: Object.entries(failureReasons)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([reason, count]) => ({ reason, count }))
        };
    };

    const getPerformanceGrade = (successRate: number) => {
        if (successRate >= 0.95) return { grade: 'A+', color: '#28a745', desc: 'Excellent' };
        if (successRate >= 0.90) return { grade: 'A', color: '#28a745', desc: 'Very Good' };
        if (successRate >= 0.85) return { grade: 'B+', color: '#ffc107', desc: 'Good' };
        if (successRate >= 0.80) return { grade: 'B', color: '#ffc107', desc: 'Acceptable' };
        if (successRate >= 0.70) return { grade: 'C', color: '#fd7e14', desc: 'Needs Improvement' };
        if (successRate >= 0.60) return { grade: 'C-', color: '#fd7e14', desc: 'Needs Improvement' };
        return { grade: 'D', color: '#dc3545', desc: 'Poor' };
    };

    const handleExportReport = () => {
        const overallMetrics = calculateMetrics(executionData);
        const reportMode = viewMode === 'current' ? 'Current Session' : `Historical (${selectedTimeRange})`;

        let reportContent = `TestWerk.ai Performance Report - ${reportMode}\n`;
        reportContent += `Generated: ${new Date().toLocaleString()}\n\n`;

        reportContent += `=== OVERALL METRICS ===\n`;
        reportContent += `Total Executions: ${overallMetrics.total}\n`;
        reportContent += `Passed: ${overallMetrics.passed}\n`;
        reportContent += `Failed: ${overallMetrics.failed}\n`;
        reportContent += `Success Rate: ${(overallMetrics.successRate * 100).toFixed(1)}%\n\n`;

        agents.forEach(agent => {
            const agentMetrics = calculateMetrics(executionData, agent.name);
            if (agentMetrics.total > 0) {
                reportContent += `=== ${agent.name.toUpperCase()} AGENT ===\n`;
                reportContent += `Executions: ${agentMetrics.total}\n`;
                reportContent += `Success Rate: ${(agentMetrics.successRate * 100).toFixed(1)}%\n\n`;
            }
        });

        const blob = new Blob([reportContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `performance_report_${viewMode}_${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (isOpen && agents.length > 0) {
            generateReport();
        }
    }, [isOpen, selectedTimeRange, viewMode, agents.length]);

    if (!isOpen) return null;

    const resultsArray = Array.from(results.values());
    const passedTests = resultsArray.filter(r => r.status === 'PASSED').length;
    const failedTests = resultsArray.filter(r => r.status === 'FAILED').length;
    const totalTests = resultsArray.length;
    const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;
    
    const avgConfidence = resultsArray.length > 0 
        ? resultsArray.reduce((sum, r) => sum + (r.ai_confidence || 0), 0) / resultsArray.length 
        : 0;

    const overallMetrics = calculateMetrics(executionData);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}
                 style={{width: '95vw', maxWidth: '1400px', maxHeight: '90vh', overflow: 'auto'}}>
                
                <div className="modal-header" style={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '1.5rem'
                }}>
                    <h2>TestWerk.ai Analytics Dashboard</h2>
                    <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <button
                            onClick={handleExportReport}
                            className="pushable push-secondary btn-sm"
                            disabled={isLoading || executionData.length === 0}
                        >
                            <span className="shadow"></span>
                            <span className="edge"></span>
                            <span className="front">Export Report</span>
                        </button>
                        <button onClick={onClose} style={{
                            background: 'none', 
                            border: 'none', 
                            fontSize: '1.5rem', 
                            cursor: 'pointer'
                        }}>
                            ✕
                        </button>
                    </div>
                </div>

                <div className="analytics-tabs" style={{
                    display: 'flex', 
                    marginBottom: '1.5rem', 
                    borderBottom: '1px solid #ddd'
                }}>
                    {[
                        { key: 'performance', label: 'AI Performance' },
                        { key: 'usage', label: 'Model Usage' },
                        { key: 'agents', label: 'Agent Analytics' },
                        { key: 'costs', label: 'Cost Analysis' }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as any)}
                            style={{
                                padding: '0.75rem 1.5rem',
                                border: 'none',
                                borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
                                backgroundColor: 'transparent',
                                color: activeTab === tab.key ? '#007bff' : '#666',
                                cursor: 'pointer',
                                fontWeight: activeTab === tab.key ? 'bold' : 'normal'
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'performance' && (
                    <div className="performance-analytics">
                        <h3 style={{marginBottom: '1.5rem'}}>AI Validation Performance</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem'
                        }}>
                            <div style={{
                                textAlign: 'center',
                                padding: '1.5rem',
                                background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                                borderRadius: '12px'
                            }}>
                                <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#155724'}}>
                                    {successRate.toFixed(1)}%
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#155724'}}>
                                    AI Validation Accuracy
                                </div>
                            </div>
                            <div style={{
                                textAlign: 'center',
                                padding: '1.5rem',
                                background: 'linear-gradient(135deg, #cce5ff 0%, #b3d9ff 100%)',
                                borderRadius: '12px'
                            }}>
                                <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#0c5460'}}>
                                    {(avgConfidence * 100).toFixed(1)}%
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#0c5460'}}>
                                    Avg AI Confidence
                                </div>
                            </div>
                            <div style={{
                                textAlign: 'center',
                                padding: '1.5rem',
                                background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                                borderRadius: '12px'
                            }}>
                                <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#856404'}}>
                                    {agents.length}
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#856404'}}>
                                    Active Agents
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'usage' && (
                    <div className="usage-analytics">
                        <h3 style={{marginBottom: '1.5rem'}}>AI Model Usage</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1rem'
                        }}>
                            <div style={{
                                padding: '1.5rem',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h4 style={{color: '#333 !important', marginBottom: '1rem'}}>Current Model</h4>
                                <div style={{fontSize: '1.8rem', fontWeight: 'bold', color: '#007bff !important'}}>
                                    {aiConfig?.default_model || 'Not configured'}
                                </div>
                            </div>
                            <div style={{
                                padding: '1.5rem',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h4 style={{color: '#333 !important', marginBottom: '1rem'}}>Total Token Usage</h4>
                                <div style={{fontSize: '1.8rem', fontWeight: 'bold', color: '#28a745 !important'}}>
                                    {totalAIUsage.tokens.toLocaleString()}
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#dc2626 !important'}}>
                                    Total Cost: ${totalAIUsage.cost.toFixed(4)}
                                </div>
                                <div style={{fontSize: '0.8rem', color: '#666 !important', marginTop: '0.5rem'}}>
                                    Test Executions: {overallMetrics.totalTokens.toLocaleString()} tokens
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'agents' && (
                    <div className="agent-analytics">
                        <h3 style={{marginBottom: '1.5rem'}}>Agent Performance Report</h3>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '6px'
                        }}>
                            <div>
                                <label style={{fontWeight: 'bold', marginBottom: '0.5rem', display: 'block'}}>View Mode:</label>
                                <select
                                    value={viewMode}
                                    onChange={(e) => setViewMode(e.target.value as 'current' | 'historical')}
                                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
                                >
                                    <option value="current">Current Session</option>
                                    <option value="historical">Historical Data</option>
                                </select>
                            </div>

                            {viewMode === 'historical' && (
                                <div>
                                    <label style={{fontWeight: 'bold', marginBottom: '0.5rem', display: 'block'}}>Time Range:</label>
                                    <select
                                        value={selectedTimeRange}
                                        onChange={(e) => setSelectedTimeRange(e.target.value)}
                                        style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
                                    >
                                        <option value="7d">Last 7 days</option>
                                        <option value="30d">Last 30 days</option>
                                        <option value="90d">Last 90 days</option>
                                    </select>
                                </div>
                            )}

                            <div>
                                <label style={{fontWeight: 'bold', marginBottom: '0.5rem', display: 'block'}}>Data Source:</label>
                                <div style={{padding: '0.5rem', color: isLoading ? '#007bff' : error ? '#dc3545' : '#28a745', fontWeight: 'bold'}}>
                                    {isLoading ? 'Loading...' : error ? 'Error' : 'Real Data'}
                                </div>
                            </div>
                        </div>

                        {isLoading ? (
                            <div style={{textAlign: 'center', padding: '3rem'}}>
                                <div>Analyzing test execution data...</div>
                            </div>
                        ) : error ? (
                            <div style={{textAlign: 'center', padding: '3rem', color: '#dc3545'}}>
                                <div>{error}</div>
                                <button
                                    onClick={generateReport}
                                    className="pushable push-primary btn-sm"
                                    style={{marginTop: '1rem'}}
                                >
                                    <span className="shadow"></span>
                                    <span className="edge"></span>
                                    <span className="front">Retry</span>
                                </button>
                            </div>
                        ) : (
                            <>
                                {overallMetrics.total > 0 && (
                                    <div style={{marginBottom: '2rem'}}>
                                        <h4>Overall Performance - {viewMode === 'current' ? 'Current Session' : 'Historical Data'}</h4>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                            gap: '1rem',
                                            marginTop: '1rem'
                                        }}>
                                            <div style={{
                                                padding: '1rem',
                                                backgroundColor: '#e8f5e8',
                                                borderRadius: '8px',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32'}}>
                                                    {overallMetrics.passed}
                                                </div>
                                                <div style={{color: '#666', fontSize: '0.8rem'}}>Passed</div>
                                            </div>
                                            <div style={{
                                                padding: '1rem',
                                                backgroundColor: '#ffebee',
                                                borderRadius: '8px',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#d32f2f'}}>
                                                    {overallMetrics.failed}
                                                </div>
                                                <div style={{color: '#666', fontSize: '0.8rem'}}>Failed</div>
                                            </div>
                                            <div style={{
                                                padding: '1rem',
                                                backgroundColor: '#fff3e0',
                                                borderRadius: '8px',
                                                textAlign: 'center'
                                            }}>
                                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#f57c00'}}>
                                                    {(overallMetrics.successRate * 100).toFixed(1)}%
                                                </div>
                                                <div style={{color: '#666', fontSize: '0.8rem'}}>Success Rate</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <h4>Individual Agent Performance</h4>
                                    <div style={{marginTop: '1rem'}}>
                                        {agents.map(agent => {
                                            const agentMetrics = calculateMetrics(executionData, agent.name);
                                            if (agentMetrics.total === 0) return null;

                                            const grade = getPerformanceGrade(agentMetrics.successRate);

                                            return (
                                                <div key={agent.name} style={{
                                                    border: '1px solid #ddd',
                                                    borderRadius: '8px',
                                                    padding: '1.5rem',
                                                    marginBottom: '1rem',
                                                    backgroundColor: '#fff'
                                                }}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                                                        <h4 style={{margin: 0}}>{agent.name}</h4>
                                                        <div style={{
                                                            backgroundColor: grade.color,
                                                            color: 'white',
                                                            padding: '0.5rem 1rem',
                                                            borderRadius: '20px',
                                                            fontWeight: 'bold',
                                                            textAlign: 'center'
                                                        }}>
                                                            <div style={{fontSize: '1.2rem'}}>{grade.grade}</div>
                                                            <div style={{fontSize: '0.8rem'}}>{grade.desc}</div>
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                                                        gap: '1rem'
                                                    }}>
                                                        <div>
                                                            <div style={{color: '#666', fontSize: '0.8rem'}}>Total</div>
                                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#333'}}>{agentMetrics.total}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{color: '#666', fontSize: '0.8rem'}}>Passed</div>
                                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745'}}>{agentMetrics.passed}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{color: '#666', fontSize: '0.8rem'}}>Failed</div>
                                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#dc3545'}}>{agentMetrics.failed}</div>
                                                        </div>
                                                        <div>
                                                            <div style={{color: '#666', fontSize: '0.8rem'}}>Success Rate</div>
                                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: grade.color}}>
                                                                {(agentMetrics.successRate * 100).toFixed(1)}%
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{color: '#666', fontSize: '0.8rem'}}>AI Confidence</div>
                                                            <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1'}}>
                                                                {(agentMetrics.avgConfidence * 100).toFixed(1)}%
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {agentMetrics.failureReasons.length > 0 && (
                                                        <div style={{marginTop: '1rem'}}>
                                                            <div style={{color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                                                                Top Failure Reasons:
                                                            </div>
                                                            <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                                                                {agentMetrics.failureReasons.map((failure, index) => (
                                                                    <span key={index} style={{
                                                                        padding: '0.25rem 0.5rem',
                                                                        backgroundColor: '#ffebee',
                                                                        color: '#d32f2f',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.8rem'
                                                                    }}>
                                                                        {failure.reason} ({failure.count}x)
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'costs' && (
                    <div className="cost-analytics">
                        <h3 style={{marginBottom: '1.5rem'}}>Cost Analysis</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1rem'
                        }}>
                            <div style={{
                                padding: '1.5rem',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h4 style={{color: '#333', marginBottom: '1rem'}}>Total Session Cost</h4>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#dc2626'}}>
                                    ${totalAIUsage.cost.toFixed(4)}
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#666'}}>Total API usage charges</div>
                                <div style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>
                                    Test executions: ${overallMetrics.totalCost.toFixed(4)}
                                </div>
                            </div>
                            <div style={{
                                padding: '1.5rem',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h4 style={{color: '#333', marginBottom: '1rem'}}>Monthly Estimate</h4>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#fd7e14'}}>
                                    ${(totalAIUsage.cost * 30).toFixed(2)}
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#666'}}>Based on total session usage</div>
                            </div>
                            <div style={{
                                padding: '1.5rem',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                            }}>
                                <h4 style={{color: '#333', marginBottom: '1rem'}}>Cost per Test Case</h4>
                                <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#28a745'}}>
                                    ${overallMetrics.total > 0 ? (totalAIUsage.cost / overallMetrics.total).toFixed(4) : '0.0000'}
                                </div>
                                <div style={{fontSize: '0.9rem', color: '#666'}}>Total cost ÷ test executions</div>
                                <div style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>
                                    {overallMetrics.total} test cases executed
                                </div>
                            </div>
                        </div>
                        
                        <div style={{
                            marginTop: '2rem',
                            padding: '1.5rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px'
                        }}>
                            <h4 style={{color: '#333', marginBottom: '1rem'}}>Cost Optimization Tips</h4>
                            <ul style={{margin: 0, fontSize: '0.9rem', color: '#666'}}>
                                <li>Enable caching to reduce repeat API calls</li>
                                <li>Use batch processing for multiple test validations</li>
                                <li>Consider switching to more cost-effective models for simple validations</li>
                                <li>Implement smart retry logic to avoid unnecessary re-runs</li>
                            </ul>
                        </div>
                    </div>
                )}

                <div className="modal-actions" style={{marginTop: '2rem'}}>
                    <button onClick={onClose} className="pushable push-primary">
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Close Dashboard</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboardModal;